import PrecisionService from '@core/services/PrecisionService';

import {
  GetMedicalPlanCoversListResponse,
  IFacultativeDetail,
  IPolicyCoverForm,
  IPropertyRiskDetail
} from '../types';
import { GetPlanCoversListResponse } from '../types/GetPlanCoversList.types';

export function mapToPlanCoversLovs(data?: GetPlanCoversListResponse) {
  const out: Record<string, string> = {};
  const items = data?.planConfig?.getPlanPlanCovers?.items ?? [];
  for (const it of items) {
    const id = it?.id;
    if (!id) continue;
    const label = [it?.cover?.code, it?.cover?.name]
      .filter(Boolean)
      .join(' - ');
    out[id] = label || id;
  }

  return out;
}

export function mapToMedicalPlanCoversLovs(
  data?: GetMedicalPlanCoversListResponse
) {
  const out: Record<string, string> = {};
  const items = data?.planConfig?.getPolicyMedicalCovers?.items ?? [];
  for (const it of items) {
    const id = it?.id;
    if (!id) continue;
    const label = [it?.code, it?.name].filter(Boolean).join(' - ');
    out[id] = label || id;
  }

  return out;
}

export function normalizeFromPlanItem(
  item: any,
  prev: IPolicyCoverForm
): IPolicyCoverForm {
  const id = item?.cover?.id ?? item?.coverID ?? prev?.policyCover ?? '';
  const premiumType =
    item?.premiumType ?? item?.coverPremiumType ?? prev?.coverPremiumType ?? '';

  const excessType =
    item?.excessOnClaimType ??
    item?.excessType ??
    prev?.excessOnClaimType ??
    '';

  let excessAmount: number | undefined;
  let excessPercentage: number | undefined;
  let excessDays: number | undefined;

  if (excessType === 'AMOUNT') {
    excessAmount =
      item?.excessOnClaimAmount ??
      item?.excessAmount ??
      prev?.excessOnClaimAmount;
  } else if (excessType === 'PERCENTAGE') {
    excessPercentage =
      item?.excessOnClaimPercentage ??
      item?.excessAmount ??
      prev?.excessOnClaimPercentage;
  } else if (excessType === 'DAYS') {
    excessDays =
      item?.excessOnClaimDays ?? item?.excessAmount ?? prev?.excessOnClaimDays;
  }

  return {
    policyCover: id,
    isMain: !!(item?.isMain ?? prev?.isMain),
    isMandatory: !!(item?.isMandatory ?? prev?.isMandatory),
    isEditable: item?.isEditable ?? true,
    coverEffectiveFrom:
      item?.coverEffectiveFrom ??
      item?.effectiveFrom ??
      prev?.coverEffectiveFrom ??
      '',
    coverEffectiveTo:
      item?.coverEffectiveTo ??
      item?.effectiveTo ??
      prev?.coverEffectiveTo ??
      '',
    sumInsuredIsAdditive: !!(
      item?.sumInsuredIsAdditive ?? prev?.sumInsuredIsAdditive
    ),
    coverSumInsured:
      item?.coverSumInsured ?? item?.sumInsured ?? prev?.coverSumInsured,
    coverPremiumType: premiumType,
    coverPremiumAmount: item?.premiumAmount,
    coverPremiumPercentage: item?.premiumPercentage,
    excessOnClaimType: excessType,
    excessOnClaimAmount: excessAmount,
    excessOnClaimPercentage: excessPercentage,
    excessOnClaimDays: excessDays
  };
}

export function sumCededSumInsured(items?: IFacultativeDetail[] | null) {
  const arr = items ?? [];

  return arr.reduce((acc, it) => acc + Number(it?.cededSumInsured), 0);
}

export function sumCededPremium(items?: IFacultativeDetail[] | null) {
  const arr = items ?? [];

  return arr.reduce((acc, it) => acc + Number(it?.cededPremium), 0);
}

export function isSumInsuredUpdateBlocked(
  items: IFacultativeDetail[],
  nextSumInsured: unknown
) {
  return sumCededSumInsured(items) > Number(nextSumInsured);
}

export function isPremiumUpdateBlocked(
  items: IFacultativeDetail[],
  nextPremiumAmount: unknown
) {
  return sumCededPremium(items) > Number(nextPremiumAmount);
}

export function parseDistributionAmount(value: unknown): number {
  if (value == null || value === '') return 0;
  const n = Number(String(value).replace(/,/g, ''));
  return Number.isNaN(n) ? 0 : n;
}

export function sumCoverDistributionForPlanCover(
  propertyRiskDetails: IPropertyRiskDetail[] | undefined,
  planCoverId: string
) {
  if (!planCoverId) return { sumInsured: 0, premium: 0 };

  const id = String(planCoverId);
  let sumInsured = 0;
  let premium = 0;

  for (const risk of propertyRiskDetails ?? []) {
    for (const pc of risk.planCovers ?? []) {
      if (String(pc.planCoverID) !== id) continue;
      sumInsured += parseDistributionAmount(pc.sumInsured);
      premium += parseDistributionAmount(pc.premium);
    }
  }

  return {
    sumInsured: PrecisionService.round(sumInsured, 3),
    premium: PrecisionService.round(premium, 3)
  };
}
